import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type { SignUpCompany } from '@/pinia-store/useSignUpStore'
import { ServiceError } from '@/utils/apiError'
import {
  BasicCompany,
  CompanyResponse,
  DoctorDetailResponse,
  ListCompanyDoctorsResponse,
  ListCompanyUserResponse,
  ListDoctorsCompanyResponse,
  UpdatedUserResponse
} from '../Types/Responses/CompanyResponses'
import {
  CompanyDoctorInvite,
  CompanyDoctorInviteSearchParams,
  CompanyUser,
  CompanyUserChangePassword,
  UpdateUserPayload
} from '../Types/Requests/CompanyRequests'
import { SelectOption } from '@/types'

export default class CompanyService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/companies'
  }

  async listAdminAccess(): Promise<SelectOption[]> {
    try {
      const { data } = await request.get<SelectOption[]>(`${this.getCompletePath()}/list-admin`)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as empresas que você administra.')
    }
  }

  async listViewAccess(): Promise<SelectOption[]> {
    try {
      const { data } = await request.get<SelectOption[]>(`${this.getCompletePath()}/list-view`)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as empresas disponíveis.')
    }
  }

  async inviteDoctorForCompany(data: CompanyDoctorInvite) {
    try {
      return await request.post(this.getCompletePath('/invite-doctor'), data)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível vincular o médico à empresa.')
    }
  }

  async getDoctorById(doctorId: number): Promise<DoctorDetailResponse> {
    try {
      const { data } = await request.get<DoctorDetailResponse>(
        this.getCompletePath(`/mydoctors/${doctorId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os dados do médico.')
    }
  }

  async getCompanyDoctors(params: CompanyDoctorInviteSearchParams) {
    try {
      const response = await request.get<ListCompanyDoctorsResponse>(
        this.getCompletePath('/doctor'),
        { ...params }
      )
      return {
        data: response.data.data,
        count: response.data.count
      }
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os médicos da empresa.')
    }
  }

  async changeUserCompanyPassword(id: number, newPassword: CompanyUserChangePassword) {
    try {
      return await request.put(this.getCompletePath(`/user/${id}/change-password`), newPassword)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível alterar a senha do usuário.')
    }
  }

  async createCompanyByPublic(data: SignUpCompany) {
    try {
      const formatedBody = {
        name: data.userName,
        email: data.email,
        phone: data.phone1,
        password: data.password,
        confirmPassword: data.confirmPassword,
        companyName: data.companyName,
        fantasyName: data.fantasyName,
        cnpj: data.cnpj,
        companyEmail: data.corporateEmail,
        companyPhone: data.corporatePhone1,
        companyCep: data.cep,
        companyStreet: data.street,
        companyDistrict: data.district,
        companyNumber: data.number,
        companyComplement: data.complement,
        companyUf: data.state,
        companyCity: data.city
      }
      // `await` é o que torna este catch alcançável: sem ele a rejeição do axios
      // escapava crua e a mensagem do backend (ex.: CNPJ já cadastrado) se perdia.
      return await request.post(this.getCompletePath(), formatedBody)
    } catch (e) {
      throw this.handleError(
        e,
        'Ocorreu um erro ao cadastrar a empresa, por favor tente novamente.'
      )
    }
  }

  async list(): Promise<CompanyResponse> {
    try {
      const { data } = await request.get<CompanyResponse>(this.getCompletePath() + '/my')
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as suas empresas.')
    }
  }

  async listBasic(): Promise<BasicCompany[]> {
    try {
      const { data } = await request.get<BasicCompany[]>(this.getCompletePath() + '/my-basic')
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as suas empresas.')
    }
  }

  async listDoctorsByCompany(
    companyId: number,
    scheduleId: number
  ): Promise<ListDoctorsCompanyResponse> {
    try {
      const { data } = await request.get<ListDoctorsCompanyResponse>(
        `${this.getCompletePath()}/${companyId}/schedule/${scheduleId}/doctors`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os médicos da escala.')
    }
  }

  async createCompanyUser(data: CompanyUser) {
    try {
      return await request.post(this.getCompletePath('/user'), data)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível cadastrar o usuário.')
    }
  }

  async listCompanyUsers(search?: string): Promise<ListCompanyUserResponse> {
    try {
      const response = await request.get<ListCompanyUserResponse>(this.getCompletePath('/user'), {
        search: search ? search : undefined
      })
      return {
        data: response.data.data,
        count: response.data.count
      }
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os usuários da empresa.')
    }
  }

  // Acesso ao localStorage (não é chamada à API): a falha é só registrada e o
  // app segue sem a lista persistida — por isso não passa por `handleError`.
  async persistLocalCompanyList(companiesId: number[]) {
    if (companiesId) {
      try {
        localStorage.setItem('@ME:companies_id', companiesId.join())
      } catch (e) {
        console.error(e)
      }
    }
  }

  getLocalTokenCompanyList(): string | undefined {
    try {
      const companiesId = localStorage.getItem('@ME:companies_id')
      return companiesId ? companiesId : undefined
    } catch (e) {
      // fallback intencional: localStorage indisponível (modo privado, cota) —
      // o interceptor segue sem o header de empresas; não é erro de API.
      console.error(e)
    }
  }

  async updateUser(userId: number, data: UpdateUserPayload): Promise<UpdatedUserResponse> {
    // Validação local (fora do try): `ServiceError` para a mensagem chegar
    // legível ao toast do chamador — um `Error` cru cairia no fallback genérico.
    if (!userId) {
      throw new ServiceError('Usuário inválido para atualização.')
    }
    if (!data || Object.keys(data).length === 0) {
      throw new ServiceError('Nenhum dado informado para atualizar o usuário.')
    }
    try {
      const response = await request.patch(this.getCompletePath(`/user/${userId}`), data)
      return response.data as UpdatedUserResponse
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar o usuário.')
    }
  }
}
